import React from 'react';
import '../../styles/Privacy.css';
import { Helmet } from 'react-helmet';
import Breadcrumbs from '../Breadcrumbs';
import SocialMetaTags from '../SocialMetaTags';
import NativeBannerAd from '../NativeBannerAd';

const Privacy = () => {
  const breadcrumbItems = [
    { name: 'Home', path: '/' },
    { name: 'Privacy', path: '/privacy' }
  ];
  
  return (
    <div className="privacy-container">
      <Helmet>
        <title>Privacy Policy | Quick Inbox Temporary Email Service</title>
        <meta name="description" content="Read about Quick Inbox's privacy policy and how we protect your data when using our temporary email service." />
        <meta name="keywords" content="privacy policy, data protection, email privacy, personal data" />
        <link rel="canonical" href="https://quick-inbox.com/privacy" />
      </Helmet>
      
      <SocialMetaTags
        title="Privacy Policy | Quick Inbox"
        description="Read about Quick Inbox's privacy policy and how we protect your data when using our temporary email service."
        url="https://quick-inbox.com/privacy"
        image="https://quick-inbox.com/images/privacy-policy.jpg"
      />
      
      <Breadcrumbs items={breadcrumbItems} />
      
      <h1 className="privacy-title">Privacy Policy</h1>
      <p className="privacy-last-updated">Last updated: March 19, 2025</p>

      <NativeBannerAd />

      <div className="privacy-content">
        <section className="privacy-section">
          <h2 className="section-title">Introduction</h2>
          <p>
            This Privacy Policy describes Our policies and procedures on the collection, use and disclosure 
            of Your information when You use the Service and tells You about Your privacy rights and how 
            the law protects You.
          </p>

          <p>
            We use Your Personal data to provide and improve the Service. By using the Service, You agree 
            to the collection and use of information in accordance with this Privacy Policy.
          </p>
        </section>

        <section className="privacy-section">
          <h2 className="section-title">Collecting and Using Your Personal Data</h2>
          
          <h3 className="subsection-title">Types of Data Collected</h3>
          
          <div className="space-y-6">
            <div>
              <h4 className="subsection-title">Personal Data</h4>
              <p>
                While using Our Service, We may ask You to provide Us with certain personally identifiable 
                information that can be used to contact or identify You. Personally identifiable information 
                may include, but is not limited to:
              </p>
            </div>

            <div>
              <h4 className="subsection-title">Usage Data</h4>
              <p className="mb-3">
                Usage Data is collected automatically when using the Service.
              </p>
              <p className="mb-3">
                Usage Data may include information such as Your Device's Internet Protocol address (e.g. IP address), 
                browser type, browser version, the pages of our Service that You visit, the time and date of Your 
                visit, the time spent on those pages, unique device identifiers and other diagnostic data.
              </p>
              <p className="mb-3">
                When You access the Service by or through a mobile device, We may collect certain information 
                automatically, including, but not limited to, the type of mobile device You use, Your mobile 
                device unique ID, the IP address of Your mobile device, Your mobile operating system, the type 
                of mobile Internet browser You use, unique device identifiers and other diagnostic data.
              </p>
              <p>
                We may also collect information that Your browser sends whenever You visit our Service or when 
                You access the Service by or through a mobile device.
              </p>
            </div>

            <div>
              <h4 className="subsection-title">Tracking Technologies and Cookies</h4>
              <p className="mb-3">
                We use Cookies and similar tracking technologies to track the activity on Our Service and store 
                certain information. Tracking technologies used are beacons, tags, and scripts to collect and 
                track information and to improve and analyze Our Service.
              </p>
              <p className="mb-3">
                You can instruct Your browser to refuse all Cookies or to indicate when a Cookie is being sent. 
                However, if You do not accept Cookies, You may not be able to use some parts of our Service.
              </p>
              <p className="mb-3">
                Cookies can be "Persistent" or "Session" Cookies. Persistent Cookies remain on your personal 
                computer or mobile device when You go offline, while Session Cookies are deleted as soon as You 
                close your web browser.
              </p>

              <p className="mb-4">We use both session and persistent Cookies for the purposes set out below:</p>

              <div className="space-y-4 pl-4">
                <div>
                  <h5 className="font-semibold">Necessary / Essential Cookies</h5>
                  <p>Type: Session Cookies</p>
                  <p>Administered by: Us</p>
                  <p>
                    These Cookies are essential to provide You with services available through the Website and to 
                    enable You to use some of its features. Without these Cookies, the services that You have asked 
                    for cannot be provided, and We only use these Cookies to provide You with those services.
                  </p>
                </div>

                <div>
                  <h5 className="font-semibold">Functionality Cookies</h5>
                  <p>Type: Persistent Cookies</p>
                  <p>Administered by: Us</p>
                  <p>
                    These Cookies allow us to remember choices You make when You use the Website, such as remembering 
                    your login details or language preference. The purpose of these Cookies is to provide You with a 
                    more personal experience and to avoid You having to re-enter your preferences every time You use 
                    the Website.
                  </p>
                </div>

                <div>
                  <h5 className="font-semibold">Tracking and Performance Cookies</h5>
                  <p>Type: Persistent Cookies</p>
                  <p>Administered by: Third-Parties</p>
                  <p>
                    These Cookies are used to track information about traffic to the Website and how users use the 
                    Website. The information gathered via these Cookies may directly or indirectly identify you as 
                    an individual visitor.
                  </p>
                </div>
              </div>
            </div>

            <div>
              <h4 className="subsection-title">Use of Your Personal Data</h4>
              <p className="mb-3">The application may use Personal Data for the following purposes:</p>
              <ul className="privacy-list">
                <li>To provide and maintain our Service, including to monitor the usage of our Service.</li>
                <li>To manage Your Account: to manage Your registration as a user of the Service.</li>
                <li>To contact You: By email, telephone calls, SMS, or other equivalent forms of electronic communication.</li>
                <li>To provide You with news, special offers and general information about other goods, services and events.</li>
                <li>To manage Your requests: To attend and manage Your requests to Us.</li>
              </ul>
            </div>

            <div>
              <h4 className="subsection-title">Retention of Your Personal Data</h4>
              <p>
                The application will retain Your Personal Data only for as long as is necessary for the purposes set out 
                in this Privacy Policy. We will retain and use Your Personal Data to the extent necessary to comply 
                with our legal obligations, resolve disputes, and enforce our legal agreements and policies.
              </p>
            </div>
          </div>
        </section>

        <section className="privacy-section">
          <h2 className="section-title">GDPR Privacy</h2>
          <div className="space-y-6">
            <div>
              <h3 className="subsection-title">Legal Basis for Processing Personal Data under GDPR</h3>
              <p className="mb-4">We may process Personal Data under the following conditions:</p>
              <ul className="privacy-list">
                <li><strong>Consent:</strong> You have given Your consent for processing Personal Data for one or more specific purposes.</li>
                <li><strong>Performance of a contract:</strong> Provision of Personal Data is necessary for the performance of an agreement with You and/or for any pre-contractual obligations thereof.</li>
                {/* <li><strong>Legal obligations:</strong> Processing Personal Data is necessary for compliance with a legal obligation to which the Company is subject.</li> */}
                <li><strong>Vital interests:</strong> Processing Personal Data is necessary in order to protect Your vital interests or of another natural person.</li>
                {/* <li><strong>Public interests:</strong> Processing Personal Data is related to a task that is carried out in the public interest or in the exercise of official authority vested in the Company.</li> */}
                {/* <li><strong>Legitimate interests:</strong> Processing Personal Data is necessary for the purposes of the legitimate interests pursued by the Company.</li> */}
              </ul>
            </div>

            <div>
              <h3 className="subsection-title">Your Rights under the GDPR</h3>
              <p className="mb-4">You have the right under this Privacy Policy, and by law if You are within the EU, to:</p>
              <ul className="privacy-list">
                <li>Request access to Your Personal Data</li>
                <li>Request correction of Your Personal Data</li>
                <li>Object to processing of Your Personal Data</li>
                <li>Request erasure of Your Personal Data</li>
                <li>Request transfer of Your Personal Data</li>
                <li>Withdraw Your consent</li>
              </ul>
            </div>
          </div>
        </section>

        <section className="privacy-section">
          <h2 className="section-title">CCPA Privacy</h2>
          <div className="space-y-6">
            <div>
              <h3 className="subsection-title">Your Rights under the CCPA</h3>
              <p className="mb-4">Under this Privacy Policy, and by law if You are a resident of California, You have the following rights:</p>
              <ul className="privacy-list">
                <li>The right to notice</li>
                <li>The right to access / the right to request</li>
                <li>The right to say no to the sale of Personal Data</li>
                <li>The right to know about Your Personal Data</li>
                <li>The right to delete Personal Data</li>
                <li>The right not to be discriminated against</li>
              </ul>
            </div>

            <div>
              <h3 className="subsection-title">Do Not Sell My Personal Information</h3>
              <p className="mb-4">
                We do not sell personal information. However, the Service Providers we partner with (for example, 
                our advertising partners) may use technology on the Service that "sells" personal information as 
                defined by the CCPA law.
              </p>
            </div>
          </div>
        </section>

        <section className="privacy-section">
          <h2 className="section-title">Additional Privacy Information</h2>
          <div className="space-y-6">
            <div>
              <h3 className="subsection-title">Children's Privacy</h3>
              <p>
                Our Service does not address anyone under the age of 13. We do not knowingly collect personally 
                identifiable information from anyone under the age of 13.
              </p>
            </div>

            <div>
              <h3 className="subsection-title">Links to Other Websites</h3>
              <p>
                Our Service may contain links to other websites that are not operated by Us. If You click on a 
                third party link, You will be directed to that third party's site. We strongly advise You to 
                review the Privacy Policy of every site You visit.
              </p>
            </div>

            <div>
              <h3 className="subsection-title">Changes to this Privacy Policy</h3>
              <p>
                We may update our Privacy Policy from time to time. We will notify You of any changes by posting 
                the new Privacy Policy on this page and updating the "Last updated" date at the top of this 
                Privacy Policy.
              </p>
            </div>

            <div>
              <h3 className="subsection-title">Contact Us</h3>
              <p className="mb-3">
                If you have any questions about this Privacy Policy or would like to provide feedback about our Service, You can contact us:
              </p>
              <ul className="privacy-list">
                <li>
                  <strong>Email:</strong> emailmanofficial@gmail.com
                </li>
                {/* Add more contact methods here if needed in the future */}
              </ul>
            </div>
          </div>
        </section>

        <section>
          <h2>Advertisement and Third-Party Services</h2>
          <p>
            Quick Inbox displays advertisements provided by third-party ad networks including Google AdSense.
            These services may use cookies, web beacons, and similar technologies to collect information 
            about your activities on our website and other websites to provide you with targeted advertising 
            based on your browsing activities and interests.
          </p>
          <p>
            These third-party ad servers or ad networks use technology to deliver advertisements that appear 
            on our site directly to your browser. They automatically receive your IP address when this occurs. 
            Other technologies (such as cookies, JavaScript, or Web Beacons) may also be used by our site's 
            third-party ad networks to measure the effectiveness of their advertising campaigns and/or to 
            personalize the advertising content that you see on the site.
          </p>
          <p>
            Quick Inbox has no access to or control over these cookies that are used by third-party 
            advertisers. You should consult the respective privacy policies of these third-party ad servers 
            for more detailed information or for instructions on how to opt-out of certain practices.
          </p>
        </section>
      </div>
    </div>
  );
};

export default Privacy;
